// components/MMChatStatsClientPanel.js
import React, { useRef, useState } from "react";
import {
  Card, CardHeader, CardContent, Button, LinearProgress,
  Typography, Chip, Stack, Alert
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import StopIcon from "@mui/icons-material/Stop";
import { useStreamChatClient } from "../context/StreamChatProvider";

/** ←必要に応じて調整してください */
const ISO_START = "2025-08-01T00:00:00.000Z"; // 2025年8月1日以降に作成
const CHANNEL_BATCH = 2;   // 一度に処理するチャンネル数（小さくしてレート回避）
const MSG_PAGE = 20;       // メッセージのページング件数（小さく安全に）
const PAUSE_PER_CHANNEL = 3500; // チャンネルごとに必ず待機(ms)
const PAUSE_PER_MSG_PAGE = 250; // メッセージページごとの待機(ms)
const MAX_RETRY = 3;       // レート制限時の最大リトライ回数

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const MMChatStatsClientPanel = () => {
  const client = useStreamChatClient();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [doneCount, setDoneCount] = useState(0);
  const [totalEstimated, setTotalEstimated] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // 進捗用カーソル
  const offsetRef = useRef(0);
  const stopRef = useRef(false);

  /** レート制限（code 9 / "Too many requests"）に当たったら待って再試行 */
  const withRateLimitRetry = async (fn, label) => {
    let attempt = 0;
    while (true) {
      try {
        return await fn();
      } catch (e) {
        const msg = String(e?.message || e);
        const isRL =
          e?.code === 9 ||
          /Too many requests/i.test(msg) ||
          /rate limit/i.test(msg);
        if (!isRL || attempt >= MAX_RETRY) {
          throw e;
        }
        const wait = (attempt + 1) * 5000; // 5s, 10s, 15s…
        console.warn(`⚠️ Rate limited at ${label}. Wait ${wait}ms then retry (${attempt + 1}/${MAX_RETRY})`);
        await sleep(wait);
        attempt += 1;
      }
    }
  };

  /** 1チャンネルの集計（本体メッセ + ユニーク発言者） */
  const countChannelStats = async (channel) => {
    let messageCount = 0;
    const speakers = new Set();
    let msgCursor;
    let first = true;

    while (true) {
      const resp = await withRateLimitRetry(
        () =>
          channel.query(
            {},
            {},
            {
              messages: { limit: MSG_PAGE, id_lt: msgCursor },
              state: first,   // ★最初の1回だけ state を取る（membersをロード）
              watch: false,
              presence: false,
            }
          ),
        `channel.query(${channel.id})`
      );
      first = false;

      const msgs = resp?.messages || [];
      if (!msgs.length) break;

      for (const m of msgs) {
        if (m.type === "regular" && !m.deleted_at) {
          messageCount += 1;
          if (m.user?.id) speakers.add(m.user.id);
        }
      }

      msgCursor = msgs[msgs.length - 1].id;
      if (msgs.length < MSG_PAGE) break;

      await sleep(PAUSE_PER_MSG_PAGE);
    }

    const membersCount = Object.keys(channel.state?.members || {}).length;
    return { messageCount, membersCount, participants: speakers.size };
  };

  /** 1バッチ（CHANNEL_BATCH件）を処理 */
  const runOneBatch = async () => {
    setErrorMsg("");
    const offset = offsetRef.current;

    const channels = await withRateLimitRetry(
      () =>
        client.queryChannels(
          { created_at: { $gte: ISO_START } },
          { created_at: -1 },
          { limit: CHANNEL_BATCH, offset, watch: false, state: false }
        ),
      `queryChannels(offset=${offset})`
    );

    if (!channels.length) {
      setProgress("これ以上のチャンネルはありません。");
      return false; // no more
    }

    // 初回見積り
    if (offset === 0) {
      // ざっくり見積：最初のページ数で概算（正確な総数は取れないためUI目安だけ）
      setTotalEstimated(`~${channels.length}+`);
    }

    for (const ch of channels) {
      if (stopRef.current) return false;

      setProgress(`集計中: ${ch.id}`);
      const stats = await countChannelStats(ch);

      // 🔵 整形ログ出力（ご要望のフォーマット）
      console.log(
        `id: ${ch.id}\n` +
          `メンバー数: ${stats.membersCount}\n` +
          `チャット総数: ${stats.messageCount}\n` +
          `チャット参加数: ${stats.participants}\n` +
          "--------------------------"
      );

      setDoneCount((v) => v + 1);
      await sleep(PAUSE_PER_CHANNEL); // チャンネルごとにしっかり待つ
    }

    offsetRef.current = offset + channels.length;
    setProgress(`進捗: ${offsetRef.current}件処理`);
    return true; // still has more (maybe)
  };

  /** 最初から開始 */
  const start = async () => {
    if (!client) return;
    stopRef.current = false;
    offsetRef.current = 0;
    setDoneCount(0);
    setProgress("開始…");
    setLoading(true);
    try {
      const more = await runOneBatch();
      if (!more) setProgress("完了（または対象なし）");
    } catch (e) {
      console.error(e);
      setErrorMsg(String(e?.message || e));
      setProgress("エラーで停止");
    } finally {
      setLoading(false);
    }
  };

  /** 続きを実行（次のバッチだけ進める） */
  const nextBatch = async () => {
    if (!client) return;
    stopRef.current = false;
    setLoading(true);
    try {
      const more = await runOneBatch();
      if (!more) setProgress("完了（これ以上なし）");
    } catch (e) {
      console.error(e);
      setErrorMsg(String(e?.message || e));
      setProgress("エラーで停止");
    } finally {
      setLoading(false);
    }
  };

  /** 停止 */
  const stop = () => {
    stopRef.current = true;
    setProgress("停止リクエスト中…（現在のチャンネルが終わると止まります）");
  };

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<BarChartIcon />}
        title="チャット集計（クライアント・ゆっくり安全版）"
        subheader={`${ISO_START} 以降に作成されたチャンネルを少しずつ処理します`}
      />
      {loading && <LinearProgress />}
      <CardContent>
        <Stack spacing={2}>
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={start}
              disabled={loading}
            >
              最初から実行
            </Button>
            <Button
              variant="outlined"
              startIcon={<SkipNextIcon />}
              onClick={nextBatch}
              disabled={loading}
            >
              次のバッチだけ実行
            </Button>
            <Button
              color="warning"
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={stop}
              disabled={!loading}
            >
              停止
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`処理済: ${doneCount}`} color="primary" />
            <Typography variant="body2" color="text.secondary">
              {progress}（推定総数: {totalEstimated || "取得中/不明"}）
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            進捗と各チャンネルの集計はコンソールに出力されます。
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MMChatStatsClientPanel;
